import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { injectedLocalApi } from '@/services/local'
import { injectedOmsApi } from '@/services/oms'
import { appendSelectedItems } from '@/utils/promotion'

type PlainInfo<T> = {
  status: RequestState
  error?: string
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface OrderState {
  storeOrder: PlainInfo<Oms.StoreOrder>
  eligiblePromotions: PlainInfo<Oms.EligiblePromotions>
  selectedPromotions: PlainInfo<SelectedPromotion[]>
  availableTimeSlots: PlainInfo<Oms.AvailableTimeSlots[]>
  updatingQuantity: PlainInfo<{
    orderId: string | null
  }>
}

const initialState: OrderState = {
  storeOrder: {
    status: 'none',
    data: {} as Oms.StoreOrder,
  },
  eligiblePromotions: {
    status: 'none',
    data: {} as Oms.EligiblePromotions,
  },
  selectedPromotions: {
    status: 'none',
    data: [] as SelectedPromotion[],
  },
  availableTimeSlots: {
    status: 'none',
    data: [] as Oms.AvailableTimeSlots[],
  },
  updatingQuantity: {
    status: 'none',
    data: {
      orderId: null,
    },
  },
}

export const orderSlice = createSlice({
  name: '@order',
  initialState,
  reducers: {
    togglePromotion: (
      state,
      action: PayloadAction<{ promotion: SelectedPromotion }>
    ) => {
      const promotion = action.payload.promotion
      const idx = state.selectedPromotions.data.findIndex(
        (p) => p.id === promotion.id
      )
      if (idx < 0) {
        if (promotion.isPromoCodePromotion && promotion.stackableOption) {
          state.selectedPromotions.data = state.selectedPromotions.data.filter(
            (p) => p.isPromoCodePromotion
          )
        }
        state.selectedPromotions.data.push(promotion)
      } else {
        state.selectedPromotions.data.splice(idx, 1)
      }
    },
    setIsUpdatingQuantity: (
      state,
      action: PayloadAction<{
        status: 'fulfilled' | 'rejected' | 'none' | 'pending'
        orderId: string | null
      }>
    ) => {
      state.updatingQuantity.status = action.payload.status
      state.updatingQuantity.data.orderId = action.payload.orderId
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        injectedLocalApi.endpoints.getStoreOrder.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          state.storeOrder.data = action.payload.data.payload.storeOrder
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.getStoreOrder.matchPending,
        (state) => {
          state.storeOrder.status = 'pending'
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updatePickupLocation.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString()
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.syncOrderMethod.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString()
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateDeliveryLocation.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString()
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateDeliveryAddressTwo.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString()
            )
          }
          if (action.payload.data?.payload?.address) {
            state.storeOrder.data.deliveryAddress =
              action.payload.data.payload.address
            state.storeOrder.data.deliveryAddressTwo =
              action.payload.data.payload.addressTwo
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateCollectionDateTime.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString()
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
      .addMatcher(
        injectedOmsApi.endpoints.getTimeSlots.matchFulfilled,
        (state, action) => {
          state.availableTimeSlots.status = 'fulfilled'
          if (action.payload?.payload?.timeIntervals) {
            state.availableTimeSlots.data = action.payload.payload.timeIntervals
          }
        }
      )
      .addMatcher(
        injectedOmsApi.endpoints.getTimeSlots.matchPending,
        (state) => {
          state.availableTimeSlots.status = 'pending'
          state.availableTimeSlots.data = []
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.addItemToCart.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString()
            )
          }
          if (action.payload?.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateStoreOrderItemQuantity.matchPending,
        (state, action) => {
          state.storeOrder.status = 'pending'
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateStoreOrderItemQuantity.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (action.payload?.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateStoreOrderItemQuantity.matchRejected,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
        }
      )
      .addMatcher(
        injectedOmsApi.endpoints.getEligiblePromotions.matchPending,
        (state) => {
          state.eligiblePromotions.status = 'pending'
        }
      )
      .addMatcher(
        injectedOmsApi.endpoints.getEligiblePromotions.matchFulfilled,
        (state, action) => {
          state.eligiblePromotions.status = 'fulfilled'
          state.eligiblePromotions.data = action.payload.payload
          if (state.storeOrder.data.orderPromotions) {
            state.selectedPromotions.data = appendSelectedItems(
              state.eligiblePromotions.data.promotions,
              state.storeOrder.data.orderPromotions,
              state.storeOrder.data.orderItems
            ).concat(
              appendSelectedItems(
                state.eligiblePromotions.data.promoCodePromotions,
                state.storeOrder.data.orderPromotions,
                state.storeOrder.data.orderItems
              )
            )
          }
        }
      )
      .addMatcher(
        injectedLocalApi.endpoints.clearCart.matchFulfilled,
        (state, action) => {
          if (action.payload?.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        }
      )
  },
})

export const { togglePromotion, setIsUpdatingQuantity } = orderSlice.actions
